import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Paper,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  DataGridPro,
} from '@mui/x-data-grid-pro';
import {
  Home as HomeIcon,
  FolderOpen as FolderOpenIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';

// Import the common toast components
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { useUserRights } from '../../../context/UserRightsContext';
import { FC } from '../../../constants/functionCodes';
import {
  filterWorkflowsByProductScope,
  workflowProductFunctionCode,
  workflowProductTitle,
} from './workflowProductScope';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateUploadOutlinedSx,
  harmonateToolbarButtonSx,
} from '../../../theme/harmonateStyles';

const harmonateBreadcrumbSx = {
  fontSize: '0.9rem',
  '& .MuiBreadcrumbs-li': { color: ht.white },
  '& a': { color: ht.white },
  '& .MuiBreadcrumbs-separator': { color: ht.white },
};

// Helper to get quarter date ranges
const getQuarterRanges = (year) => ([
  {
    label: `Q1 ${year}`,
    from: `${year}-01-01`,
    to: `${year}-03-31`,
  },
  {
    label: `Q2 ${year}`,
    from: `${year}-04-01`,
    to: `${year}-06-30`,
  },
  {
    label: `Q3 ${year}`,
    from: `${year}-07-01`,
    to: `${year}-09-30`,
  },
  {
    label: `Q4 ${year}`,
    from: `${year}-10-01`,
    to: `${year}-12-31`,
  },
]);

// Helper to get current quarter index
const getCurrentQuarterIndex = (date = new Date()) => {
  const month = date.getUTCMonth(); // 0-based
  if (month < 3) return 0;
  if (month < 6) return 1;
  if (month < 9) return 2;
  return 3;
};

const ReferenceDrawer = ({ onBackToWorkflows, onNavigateToReferenceFiles, productScope = null }) => {
  // State for reference data and quarter selection
  const [referenceData, setReferenceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quarterAnchorEl, setQuarterAnchorEl] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [quarterRanges, setQuarterRanges] = useState([]);
  
  // Use the common toast hook
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();
  const { hasPermission, isHarmonateUser } = useUserRights();
  const scopedFunctionCode = workflowProductFunctionCode(productScope);
  const canView = isHarmonateUser
    || (scopedFunctionCode
      ? hasPermission(scopedFunctionCode, 'view')
      : (hasPermission(FC.DOCUMENT_VALIDATION, 'view') || hasPermission(FC.ETVL_ENGINE, 'view')));
  const pageTitle = workflowProductTitle(productScope);

  // Initialize quarters on component mount
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const quarters = getQuarterRanges(currentYear);
    setQuarterRanges(quarters);
    // Set "All Quarters" as default
    setSelectedQuarter('all');
  }, []);

  // Handle quarter menu open
  const handleQuarterClick = (event) => {
    setQuarterAnchorEl(event.currentTarget);
  };

  // Handle quarter menu close
  const handleQuarterClose = () => {
    setQuarterAnchorEl(null);
  };

  // Handle quarter selection
  const handleQuarterSelect = (quarterLabel) => {
    handleQuarterClose();
    if (quarterLabel === 'All Quarters') {
      setSelectedQuarter('all');
      fetchReferenceData('all');
      showInfo('Showing all quarters', 'Quarter Changed');
    } else {
      const quarter = quarterRanges.find(q => q.label === quarterLabel);
      if (quarter) {
        setSelectedQuarter(quarter);
        fetchReferenceData(quarter);
        showInfo(`Switched to ${quarterLabel}`, 'Quarter Changed');
      }
    }
  };

  // Fetch reference data from API
  const fetchReferenceData = async (quarter) => {
    if (!quarter) return;
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      console.log(`Loading reference data for quarter: ${quarter === 'all' ? 'All Quarters' : quarter?.label}`);
      
      // Get tenant ID from local storage
      const tenantId = localStorage.getItem('tenant_id');
      if (!tenantId) {
        throw new Error('Tenant ID not found');
      }
      
      // Call the API for reference data with all required parameters
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/').replace(/\/+$/, '');
      // Create URL with unencoded parameters first
      const url = new URL(`${baseUrl}/conductor/document-types/by-tenant/basic`);
      // Append parameters without additional encoding
      url.searchParams.append('tenantId', tenantId);
      
      // Only add from/to parameters if quarter is not 'all' and is a valid quarter object
      if (quarter && quarter !== 'all' && typeof quarter === 'object' && quarter.from && quarter.to) {
        // Convert date strings to ISO 8601 format with timezone (OffsetDateTime format)
        // "from" should be start of day: "2026-01-01T00:00:00.000Z"
        // "to" should be end of day: "2026-03-31T23:59:59.999Z"
        const formatToOffsetDateTime = (dateString, isEndOfDay = false) => {
          if (isEndOfDay) {
            return `${dateString}T23:59:59.999Z`;
          }
          return `${dateString}T00:00:00.000Z`;
        };
        
        const fromDateTime = formatToOffsetDateTime(quarter.from, false);
        const toDateTime = formatToOffsetDateTime(quarter.to, true);
        url.searchParams.append('from', fromDateTime);
        url.searchParams.append('to', toDateTime);
      }
      
      url.searchParams.append('category', 'REF');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch reference data: ${response.status} ${response.statusText}`);
      }
      
      let data = await response.json();
      
      // Filter out the 'Unidentified' document type
      if (Array.isArray(data)) {
        data = data.filter(item => item.name !== 'Unidentified' && item.name !== 'Unclassified');
      }
      
      // Map API response to DataGrid rows - same structure as workflow data
      const transformedData = (Array.isArray(data) ? data : []).map((item, idx) => ({
        id: item.id || idx + 1,
        documentType: item.name || '',
        inProcess: item.inProcessCount || 0,
        awaitingApproval: item.awaitingApprovalCount || 0,
        complete: item.completedCount || 0,
        workflowId: item.id,
        tenantDocumentId: item.id,
        documentTypeId: item.id,
        createdAt: item.createdAt || '',
        updatedAt: item.updatedAt || '',
      }));

      console.log('Loaded Reference Data:', transformedData);
      setReferenceData(transformedData);
      const visibleCount = filterWorkflowsByProductScope(transformedData, productScope).length;
      
      if (visibleCount === 0) {
        showInfo('No reference data found', 'Empty Results');
      } else {
        showSuccess(`Successfully loaded ${visibleCount} reference document types`, 'Data Loaded');
      }
    } catch (err) {
      console.error('Error loading reference data:', err);
      setError(err.message);
      setReferenceData([]);
      showError(`Failed to load reference data: ${err.message}`, 'Loading Error');
    } finally {
      setLoading(false);
    }
  };

  // Load reference data when component mounts
  useEffect(() => {
    if (selectedQuarter) {
      fetchReferenceData(selectedQuarter);
    }
  }, [selectedQuarter]);

  const visibleReferenceData = useMemo(
    () => filterWorkflowsByProductScope(referenceData, productScope),
    [referenceData, productScope]
  );

  const handleRowClick = (params) => {
    if (!canView) {
      showError('You do not have permission to view workflow details', 'Access Denied');
      return;
    }
    const { documentType, documentTypeId, workflowId, tenantDocumentId } = params.row;
  
  // Create reference data object to pass to the list component
  const selectedReferenceData = {
    documentType: documentType,
    id: documentTypeId,  // Make sure this is set correctly
    documentTypeId: documentTypeId,
    workflowId: workflowId,
    tenantDocumentId: tenantDocumentId,
    inProcess: params.row.inProcess,
    awaitingApproval: params.row.awaitingApproval,
    complete: params.row.complete
  };
  
  console.log('Selected Reference Data:', selectedReferenceData); // Add this for debugging
  
  showInfo(`Opening ${documentType} reference files`, 'Navigating to Files');
  
  // Call navigation function passed as prop, also pass the selected quarter
  if (onNavigateToReferenceFiles) {
    onNavigateToReferenceFiles(selectedReferenceData, selectedQuarter);
  }
};

  const handleRetry = () => {
    if (selectedQuarter) {
      fetchReferenceData(selectedQuarter);
    }
  };

  return (
    <>
      <Box sx={{ ...harmonatePageSx, minHeight: '100vh' }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Breadcrumbs aria-label="breadcrumb" sx={harmonateBreadcrumbSx}>
              <Link
                underline="hover"
                color="inherit"
                onClick={onBackToWorkflows}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <HomeIcon sx={{ fontSize: '1rem' }} />
                {pageTitle}
              </Link>
              <Typography sx={{ color: ht.white, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FolderOpenIcon sx={{ fontSize: '1rem' }} />
                Reference Files
              </Typography>
            </Breadcrumbs>
            <Box>
              <Button
                variant="contained"
                endIcon={<KeyboardArrowDownIcon />}
                onClick={handleQuarterClick}
                sx={harmonateToolbarButtonSx}
              >
                {selectedQuarter === 'all' ? 'All Quarters' : selectedQuarter?.label || 'Select Quarter'}
              </Button>
              <Menu 
                anchorEl={quarterAnchorEl} 
                open={Boolean(quarterAnchorEl)} 
                onClose={handleQuarterClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={() => handleQuarterSelect('All Quarters')}>All Quarters</MenuItem>
                {quarterRanges.map((q) => (
                  <MenuItem key={q.label} onClick={() => handleQuarterSelect(q.label)}>
                    {q.label}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        </Box>

        {/* Loading state */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <CircularProgress sx={{ color: ht.green }} />
            <Typography variant="body2" sx={{ ml: 2 }}>Loading reference data...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <Alert severity="error" sx={{ mb: 3, maxWidth: '500px' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Failed to load reference data
              </Typography>
              <Typography variant="body2">
                {error}
              </Typography>
            </Alert>
            <Button 
              variant="contained" 
              onClick={handleRetry}
              sx={harmonateProceedButtonSx}
            >
              Retry
            </Button>
          </Box>
        ) : (
          <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, flex: 1, mb: 3, overflow: 'hidden' }}>
            <DataGridPro
              rows={visibleReferenceData}
              rowHeight={40}
              columns={[
                { field: 'documentType', headerName: 'Document Type', flex: 1 },
                { field: 'inProcess', headerName: 'In Process', width: 120, align: 'center' },
               // { field: 'awaitingApproval', headerName: 'Awaiting Approval', width: 120, align: 'center' },
                { field: 'complete', headerName: 'Completed', width: 120, align: 'right' },
              ]}
              onRowClick={handleRowClick}
              columnResizeMode="onEnd"
               pagination
              rowsPerPageOptions={[10, 25, 50]}
              pageSize={10}
              autoPageSize={false}
              resizable
              sx={{ ...harmonateDataGridSx, '& .MuiDataGrid-row:hover': { cursor: 'pointer' } }}
              disableSelectionOnClick
              noRowsOverlay={() => (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No reference data available
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={handleRetry}
                    sx={harmonateUploadOutlinedSx}
                  >
                    Refresh
                  </Button>
                </Box>
              )}
            />
          </Paper>
        )}
      </Box>

      {/* Common Toast Component */}
      <Toast 
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
        variant={toastState.variant}
      />
    </>
  );
};

export default ReferenceDrawer;